import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import CardImage from './CardImage';
import './CardViewer.css';

export const VIEWER_PAGE_DISTANCE_PX = 60;
export const VIEWER_DISMISS_DISTANCE_PX = 160;

/**
 * Achsen-Sperre: die überwiegende Richtung der Bewegung entscheidet, was sie war — ohne
 * das könnte ein diagonaler Wisch blättern UND schließen. Schließen braucht mehr Weg als
 * Blättern, weil es der teurere Fehlgriff ist.
 */
export function entscheideGeste({ offsetX, offsetY, velocityX, velocityY }) {
  if (Math.abs(offsetX) >= Math.abs(offsetY)) {
    if (offsetX < -VIEWER_PAGE_DISTANCE_PX || velocityX < -500) return { art: 'blättern', richtung: 1 };
    if (offsetX > VIEWER_PAGE_DISTANCE_PX || velocityX > 500) return { art: 'blättern', richtung: -1 };
    return { art: 'nichts' };
  }
  if (offsetY > VIEWER_DISMISS_DISTANCE_PX || velocityY > 600) return { art: 'schließen' };
  return { art: 'nichts' };
}

/**
 * Baustein — CardViewer
 *
 * Overlay-Viewer: eine Karte in voller Displaybreite über der abgedunkelten Seite,
 * Wischen (und ‹ ›) blättert durch `cards`, Tipp neben Karte/Inhalt schließt.
 *   <CardViewer open cards={cards} index={i} onIndexChange={setI} onClose={…}
 *               label="Ramp · 3 von 6">{aktionszeile}</CardViewer>
 * - `cards` ist ein Array von Objekten mit `imageUrl`, `imageUrlBack`, `name`
 *   (App-Konvention), optional `face` ('front' | 'back': Start-Seite der Karte, Standard
 *   vorne) und optional `imageOrientation` (aus dem Umschlag; Querformat-Karten liegen dann
 *   quer in voller Breite, Modus `landscape="always"` von ui/CardImage). `index` ist
 *   kontrolliert — der Aufrufer hält den Zustand.
 * - Bild über ui/CardImage (Flip doppelseitiger Karten inklusive; Kartenwechsel setzt
 *   Flip/Fehlerzustand zurück, weil CardImage auf src/backSrc-Wechsel selbst resettet).
 *   Der Karten-Slot ist zusätzlich per `index` + Bild-/Name-Referenz geschlüsselt, damit
 *   auch zwei Karten ohne `imageUrl` (beide `undefined`, z.B. während des Ladens) beim
 *   Blättern sauber remounten, statt sich denselben Motion-Node zu teilen.
 * - Gesten: framer-motion drag in beide Achsen, `dragConstraints` auf allen vier Seiten 0.
 *   `entscheideGeste` sperrt auf die überwiegende Achse — waagerecht blättert (60px oder
 *   Schwung 500), senkrecht schließt (160px oder Schwung 600); am Rand federt es über
 *   `dragElastic` zurück (kein Blättern über den ersten/letzten Index hinaus).
 * - Schließen: Tipp auf den Backdrop, Escape, ✕ oben rechts am Sichtfenster-Rand, senkrechter Wisch.
 *   Kein Fokus-Trap-Anspruch wie ein Modal — der Viewer ist eine Blätter-Fläche; body-Scroll
 *   ist gesperrt, solange er offen ist.
 * - Escape schließt nicht, wenn ein Radix-Layer darüber selbst schon reagiert hat
 *   (`defaultPrevented`) oder ein solcher Layer noch offen ist (`[data-state="open"]` mit
 *   `role="dialog"` — z.B. ein BottomSheet über dem Viewer — oder `role="menu"`, ein
 *   offenes ContextMenu) — die obere Ebene schließt zuerst. Radix hört Escape in der
 *   Capture-Phase und ruft dabei kein `preventDefault`; ohne die Sonde schlösse ein
 *   Escape beide Ebenen zugleich.
 * - z-index 400: über App-Header (200) und FilterStrip-Dock (150), unter Modal-Ebene
 *   (1000) — ein BottomSheet/Modal darf über dem Viewer öffnen.
 * - `raised` (boolean, optional): hebt den Viewer über die Modal-Ebene (1100 statt 400) — für
 *   den umgekehrten Fall, dass der Viewer AUS einem bereits offenen BottomSheet/SchemaModal
 *   heraus geöffnet wird (`CardDetailModal`s Miniatur-Tipp) und über SEINEM EIGENEN Auslöser
 *   liegen muss, nicht darunter. Ohne `raised` liegt der eigene Backdrop dieses Modals sonst
 *   über dem Viewer (z-index 1000 > 400) und fängt jeden Klick im Viewer ab — inklusive
 *   dessen eigenem ✕ —, was das Modal fälschlich mitschließt.
 */
export default function CardViewer({ open, cards = [], index = 0, onIndexChange, onClose, label, raised = false, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]')) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !cards.length) return null;

  const safeIndex = Math.min(index, cards.length - 1);
  const card = cards[safeIndex];
  const step = (delta) => {
    const next = safeIndex + delta;
    if (next >= 0 && next < cards.length) onIndexChange?.(next);
  };

  return createPortal(
    <div className={`card-viewer${raised ? ' card-viewer--raised' : ''}`} role="dialog" aria-label={card.name} onClick={onClose}>
      <div className="card-viewer__stage" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="card-viewer__close" aria-label="Schließen" onClick={onClose}>
          <X size={20} aria-hidden />
        </button>
        <motion.div
          key={`${safeIndex}-${card.imageUrl || card.name || 'card'}`}
          className="card-viewer__card"
          drag
          dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
          dragElastic={0.3}
          onDragEnd={(e, info) => {
            const geste = entscheideGeste({
              offsetX: info.offset.x,
              offsetY: info.offset.y,
              velocityX: info.velocity.x,
              velocityY: info.velocity.y,
            });
            if (geste.art === 'blättern') step(geste.richtung);
            else if (geste.art === 'schließen') onClose?.();
          }}
        >
          <CardImage src={card.imageUrl} backSrc={card.imageUrlBack || undefined} alt={card.name} defaultFace={card.face} landscape="always" orientation={card.imageOrientation} />
        </motion.div>
        <div className="card-viewer__nav">
          <button
            type="button"
            className="card-viewer__nav-btn"
            aria-label="Vorherige Karte"
            onClick={() => step(-1)}
            disabled={safeIndex === 0}
          >
            <ChevronLeft size={18} aria-hidden />
          </button>
          <span className="card-viewer__label">{label}</span>
          <button
            type="button"
            className="card-viewer__nav-btn"
            aria-label="Nächste Karte"
            onClick={() => step(1)}
            disabled={safeIndex >= cards.length - 1}
          >
            <ChevronRight size={18} aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
